import { Dialog } from '@angular/cdk/dialog';
import { ChangeDetectionStrategy, Component, DestroyRef, inject, OnInit, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { FunctionsEnum, PaisEnum } from '@app/core/models';
import { ModalLivesComponent } from '@app/modules/dashboard/components/modal-lives/modal-lives.component';
import { ModalSystematicReadComponent } from '@app/modules/eclass/components/modal-systematic-read/modal-systematic-read.component';
import { SHARED_MODULES } from '@app/shared';
import { ModalBooksHighlightedComponent } from '@app/shared/modules/modal-books-highlighted/modal-books-highlighted.component';
import { Role } from '@app/shared/roles';
import { Checker, RequiredAccessArray } from '@app/shared/utils';
import { notNull } from '@app/shared/utils/operators';
import { AppSelectors } from '@app/store';
import { AppState } from '@app/store/app.state';
import { Store } from '@ngrx/store';
import { first, flattenDeep, isNil } from 'lodash';
import { combineLatest, Observable, of, take } from 'rxjs';
import { filter, map, switchMap } from 'rxjs/operators';
import { Builder, Shortcut } from '../shortcut-list/shortcut';

interface Item extends Builder {
  [k: string]: any;
}
@Component({
  selector: 'app-shortcut-button',
  imports: [SHARED_MODULES],
  templateUrl: './shortcut-button.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ShortcutButtonComponent implements OnInit {
  protected items$: Observable<any[]>;
  private readonly _dialog = inject(Dialog);
  private readonly _destroy = inject(DestroyRef);
  private readonly _store = inject<Store<AppState>>(Store);
  private readonly _router = inject(Router);

  public readonly items: Item[] = [
    () => ({
      title: 'eclass.sistematica',
      product: 'sistematica.titulo-sistematica',
      icon: 'icon-systematic',
      style:
        'hover:bg-modules-pedagogical-600 border-b-modules-pedagogical-500 hover:border-b-modules-pedagogical-400 bg-modules-pedagogical-100 hover:border-modules-pedagogical-600 hover:text-white text-modules-pedagogical-600',
      bgStyle: 'text-modules-pedagogical-200 fill-none stroke-2',
      iconStyle: 'text-modules-pedagogical-600 w-10 h-10',
      textStyle: 'text-modules-pedagogical-700',
      load: signal(false),
      open: () => this.onOpenEducationalProjectModal(),
      permission: [20, 40],
      show: this._store.select(AppSelectors.ActiveUser).pipe(
        notNull(),
        map((activeUser) => !!activeUser?.entidade_id),
      ),
    }),
    () => ({
      title: 'materiais-didaticos.objetos-educacionais',
      product: 'menu.materiais-didaticos',
      icon: 'icon-library',
      style:
        'hover:bg-modules-educational-materials-500 border-b-modules-educational-materials-500 hover:border-b-modules-educational-materials-400 bg-modules-educational-materials-100 hover:border-modules-educational-materials-600 hover:text-white text-modules-educational-materials-600',
      bgStyle: 'text-modules-educational-materials-200',
      iconStyle: 'text-modules-educational-materials-600',
      textStyle: 'text-modules-educational-materials-700',
      url: '/materiais-didaticos',
      permission: [1, 1],
      show: this._store.select(AppSelectors.appFeature).pipe(
        filter(({ usuario_ativo }) => !!usuario_ativo),
        map((store) => Role.educationalMaterials(store)),
      ),
    }),
    () => ({
      title: 'Material 5º Ano - 2027',
      product: 'menu.materiais-didaticos',
      icon: 'icon-library',
      style:
        'hover:bg-modules-educational-materials-500 border-b-base-service-chave1-400 hover:border-b-modules-educational-materials-400 bg-modules-educational-materials-100 hover:border-modules-educational-materials-600 hover:text-white text-modules-educational-materials-600',
      bgStyle: 'text-modules-educational-materials-200',
      iconStyle: 'text-base-campfire-500',
      textStyle: 'text-base-campfire-500',
      load: signal(false),
      open: () =>
        this._dialog.open(ModalBooksHighlightedComponent, {
          data: { title: 'Material 5º Ano - 2027', padrao_serie_id: 80 },
        }).closed,
      new: true,
      permission: [1, 1],
      show: this._store.select(AppSelectors.appFeature).pipe(
        filter(({ usuario_ativo }) => !!usuario_ativo),
        map(
          (store) =>
            store.usuario_ativo?.pais_id === PaisEnum.Brasil &&
            Role.educationalMaterials(store) &&
            (Checker.isAdmin(store.usuario_ativo?.funcao_id) || Checker.isTeacher(store.usuario_ativo?.funcao_id)),
        ),
      ),
    }),
    () => ({
      title: 'Material 6º Ano - 2027',
      product: 'menu.materiais-didaticos',
      icon: 'icon-library',
      style:
        'hover:bg-modules-educational-materials-500 border-b-base-service-chave1-400 hover:border-b-modules-educational-materials-400 bg-modules-educational-materials-100 hover:border-modules-educational-materials-600 hover:text-white text-modules-educational-materials-600',
      bgStyle: 'text-modules-educational-materials-200',
      iconStyle: 'text-base-campfire-500',
      textStyle: 'text-base-campfire-500',
      load: signal(false),
      open: () =>
        this._dialog.open(ModalBooksHighlightedComponent, {
          data: { title: 'Material 6º Ano - 2027', padrao_serie_id: 90 },
        }).closed,
      new: true,
      permission: [1, 1],
      show: this._store.select(AppSelectors.appFeature).pipe(
        filter(({ usuario_ativo }) => !!usuario_ativo),
        map(
          (store) =>
            store.usuario_ativo?.pais_id === PaisEnum.Brasil &&
            !store.usuario_ativo?.externo &&
            Role.educationalMaterials(store) &&
            (Checker.isAdmin(store.usuario_ativo?.funcao_id) || Checker.isTeacher(store.usuario_ativo?.funcao_id)),
        ),
      ),
    }),
    () => ({
      title: 'menu.cursos',
      product: 'menu.trilhas-aprendizagem',
      icon: 'icon-agenda',
      style:
        'hover:bg-modules-learning-trails-600 border-b-modules-learning-trails-500 hover:border-b-modules-learning-trails-400 bg-modules-learning-trails-100 hover:border-modules-learning-trails-600 hover:text-white text-modules-learning-trails-600',
      bgStyle: 'text-modules-learning-trails-200',
      iconStyle: 'text-modules-learning-trails-600 w-10 h-10',
      textStyle: 'text-modules-learning-trails-700',
      permission: [1, 5],
      url: '/trilhas-de-aprendizagem/cursos',
    }),
    () => ({
      title: 'geral.formacao',
      product: 'menu.universidade-corporativa',
      icon: 'icon-mortarboard',
      style:
        'hover:bg-modules-corporate-university-600 border-b-modules-corporate-university-500 hover:border-b-modules-corporate-university-400 bg-modules-corporate-university-100 hover:border-modules-corporate-university-600 hover:text-white text-modules-corporate-university-600',
      bgStyle: 'text-modules-corporate-university-200',
      iconStyle: 'text-modules-corporate-university-600 w-10 h-10',
      textStyle: 'text-modules-corporate-university-700',
      url: '/universidade-corporativa/cursos',
      permission: [31, 58],
      show: this._store.select(AppSelectors.ActiveUser).pipe(
        notNull(),
        map((activeUser) => !activeUser?.escola_externa),
      ),
    }),
    () => ({
      title: 'breadcrumb.eclass.lives',
      product: 'eclass.agenda.filtros.aula-online',
      load: signal(false),
      open: () => this.onOpenLives(),
      icon: 'icon-zoom',
      style:
        'hover:bg-base-hangout-meet-500 border-b-base-hangout-meet-500 hover:border-b-base-hangout-meet-400 bg-base-hangout-meet-100 hover:border-base-hangout-meet-600 hover:text-white text-base-hangout-meet-600',
      bgStyle: 'text-base-hangout-meet-200',
      iconStyle: 'text-base-hangout-meet-600 w-10 h-10',
      textStyle: 'text-base-hangout-meet-700',
      permission: [1, 82],
    }),
  ];

  public ngOnInit() {
    this.load();
  }

  public onOpen(item: Shortcut) {
    if (!item.load()) {
      item.load.set(true);
      item
        .open()
        .pipe(take(1))
        .subscribe(() => item.load.set(false));
    }
  }

  public routerLink(item): string {
    return first(flattenDeep([item.url]));
  }

  private load() {
    this.items$ = this._store.select(AppSelectors.appFeature).pipe(
      takeUntilDestroyed(this._destroy),
      switchMap(() => combineLatest(this.items.map((item) => this.resolve(item())))),
      map((items) => items.filter(({ show, permission }) => show && permission)),
    );
  }

  private resolve(item: Shortcut): Observable<any> {
    return combineLatest({
      show: this.helperShow(item.show),
      permission: this.helperAllow(item.permission),
      item: of(item),
    }).pipe(
      take(1),
      map(({ show, permission, item }) => ({ ...item, show, permission })),
    );
  }

  private helperShow(show: Observable<boolean>): Observable<boolean> {
    if (isNil(show)) {
      return of(true);
    }
    return show;
  }

  private helperAllow(permission: RequiredAccessArray): Observable<boolean> {
    if (isNil(permission)) {
      return of(true);
    }
    return this._store.select(AppSelectors.isAllow(permission));
  }

  private onOpenEducationalProjectModal(): Observable<any> {
    const activeUser$ = this._store.select(AppSelectors.ActiveUser);

    return activeUser$.pipe(
      notNull(),
      take(1),
      switchMap((data) => this._dialog.open(ModalSystematicReadComponent, { data }).closed),
    );
  }

  private onOpenLives(): Observable<any> {
    return this._store.select(AppSelectors.ActiveUser).pipe(
      notNull(),
      take(1),
      switchMap((activeUser) => {
        const isStudentOrResponsible = [FunctionsEnum.aluno, FunctionsEnum.responsavel].includes(activeUser.funcao_id);

        if (isStudentOrResponsible) {
          return this._dialog.open(ModalLivesComponent, {
            data: activeUser,
          }).closed;
        }

        this._router.navigate(['/academico/lives']);

        return of(null);
      }),
    );
  }
}
